"use client";

import { useSelector } from "react-redux";
import { useMemo } from "react";

import { useAuth0V2 } from "@stadium/services/auth0";
import { useSendItemsContext } from "@contexts/send-items-context";
import { useStoreContext } from "@components/store/provider";
import { useConfig } from "@stadium/hooks/lib/use-config";

import { ExternalButton } from "@sharedComponents/buttons";

import { getCartOrderId } from "@selectors/cart";
import { getStoreNumber } from "@selectors/store";
import { getBookACallCtaText } from "@stadium/shared/src/selectors/book-a-call";

import { BOOK_A_CALL } from "@constants/routes";
import { SERVER_PHASE } from "@stadium/constants";

const TalkToSalesButton: typeof ExternalButton = ({
  className,
  styleType = "primary",
  children,
  ...props
}) => {
  const { isAuthenticated } = useAuth0V2();
  const orderId = useSelector(getCartOrderId) || "";
  const shopNumber = useSelector(getStoreNumber);
  const { countryCode } = useStoreContext();
  const { isSendToMyself } = useSendItemsContext();
  const bookACallCtaText = useConfig(getBookACallCtaText);

  const queryParam = useMemo(
    () =>
      btoa(
        JSON.stringify({
          ...(isAuthenticated ? { orderId } : {}),
          shopNumber,
          countryCode,
          isSendToMyself,
        }),
      ),
    [isAuthenticated, orderId, shopNumber, countryCode, isSendToMyself],
  );

  const href = useMemo(() => {
    if (SERVER_PHASE) return BOOK_A_CALL;
    const url = new URL(BOOK_A_CALL, window.location.origin);
    url.searchParams.append("orderData", queryParam);
    return url.toString().replace(window.location.origin, "");
  }, [queryParam]);

  return (
    <ExternalButton href={href} styleType={styleType} className={className} {...props}>
      {children ?? bookACallCtaText}
    </ExternalButton>
  );
};

export default TalkToSalesButton;
